/**
 * Shared mutation helpers for the three day-item slices
 * (place / transport / custom). They factor out the mechanical
 * triple-nested immer dance (`trips.map → days.map → items.filter/sort`)
 * plus the `_capture()` + `markTripDirty(tripId, ['days'])` chain that
 * every mutating action in those slices used to inline.
 *
 * Slice-specific logic — entity construction in `add*`, per-field
 * updates in `update*`, analytics tracking — stays in the slice files
 * because it doesn't deduplicate cleanly (Place carries photoRef /
 * catalogId / openingHours, Transport tracks mode changes, Custom owns
 * placeholder conversion).
 */
import type { Day } from '@my-fullstack-app/itinerary-types';
import { addMinutes, DEFAULT_START_TIME, parseTime, sortByStartTime, timeToMinutes } from '../helpers';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty } from '../sync-bridge';

type Setter = (
  updater: (s: ItineraryState) => Partial<ItineraryState> | ItineraryState,
) => void;
type Getter = () => ItineraryState;

type DayItemField = 'places' | 'transports' | 'customs';
type DayItemOf<K extends DayItemField> = NonNullable<Day[K]>[number];

function readItems<K extends DayItemField>(d: Day, field: K): DayItemOf<K>[] {
  return (d[field] ?? []);
}

function withItems<K extends DayItemField>(
  d: Day,
  field: K,
  items: DayItemOf<K>[],
): Day {
  return { ...d, [field]: items };
}

/** Remove the item with `itemId` from `day[field]`. */
export function removeDayItem<K extends DayItemField>(
  set: Setter,
  get: Getter,
  field: K,
  tripId: string,
  dayId: string,
  itemId: string,
): void {
  // Gate the snapshot + dirty mark on actual existence. Otherwise a stale
  // UI calling remove() against a tripId removed by remote sync would
  // burn a history slot AND seed the dirty queue with a phantom trip,
  // which then 404s through the [5s, 15s, 60s] backoff ladder.
  const state = get();
  const trip = state.trips.find((t) => t.id === tripId);
  const day = trip?.days.find((d) => d.id === dayId);
  if (!day || !readItems(day, field).some((e) => e.id === itemId)) return;

  state._capture();
  set((s) => ({
    trips: s.trips.map((t) => {
      if (t.id !== tripId) return t;
      return {
        ...t,
        days: t.days.map((d) => {
          if (d.id !== dayId) return d;
          return withItems(d, field, readItems(d, field).filter((e) => e.id !== itemId));
        }),
      };
    }),
  }));
  markTripDirty(tripId, ['days']);
}

/**
 * Index-based intra/cross-day move. `sortAfterMove` controls whether the
 * destination day is re-sorted by start-time after the insert — `true`
 * for transport/custom (their list invariant is time-sorted), `false`
 * for place (the user picked the explicit target index).
 */
export function moveDayItemByIndex<K extends DayItemField>(
  set: Setter,
  get: Getter,
  field: K,
  tripId: string,
  sourceDayId: string,
  sourceIndex: number,
  targetDayId: string,
  targetIndex: number,
  options?: {
    sortAfterMove?: boolean;
    onMoved?: (info: { crossDay: boolean }) => void;
  },
): void {
  // Gate snapshot + dirty mark on actual mutation. If the trip/day/item
  // lookup misses (race with a remote delete, stale UI), we'd otherwise
  // capture a phantom history entry and enqueue an unchanged trip in the
  // dirty queue.
  const state = get();
  const trip = state.trips.find((t) => t.id === tripId);
  const sourceDay = trip?.days.find((d) => d.id === sourceDayId);
  const targetDay = trip?.days.find((d) => d.id === targetDayId);
  const item = sourceDay ? readItems(sourceDay, field)[sourceIndex] : undefined;
  if (!trip || !sourceDay || !targetDay || !item) return;

  state._capture();
  const sortAfter = options?.sortAfterMove ?? false;
  set((s) => {
    const sourceItems = readItems(sourceDay, field);
    const newSource = sourceItems.filter((_, i) => i !== sourceIndex);
    const isSameDay = sourceDayId === targetDayId;
    const baseTarget = isSameDay ? newSource : readItems(targetDay, field);
    const inserted = [
      ...baseTarget.slice(0, targetIndex),
      item,
      ...baseTarget.slice(targetIndex),
    ];
    const newTarget = sortAfter ? sortByStartTime(inserted) : inserted;

    return {
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          days: t.days.map((d) => {
            if (d.id === sourceDayId)
              return withItems(d, field, isSameDay ? newTarget : newSource);
            if (d.id === targetDayId) return withItems(d, field, newTarget);
            return d;
          }),
        };
      }),
    };
  });
  markTripDirty(tripId, ['days']);
  options?.onMoved?.({ crossDay: sourceDayId !== targetDayId });
}

/**
 * Default start-time for a new item, considering ALL three lists
 * (places, transports, customs) — the new item should land after
 * whichever entry currently ends last. Used by addTransport / addCustom.
 * `addPlace` uses a places-only computation (transports between places
 * shouldn't push the next place's slot) and is intentionally not folded
 * in here.
 */
export function nextStartTimeFromAllItems(day: Day | undefined): string {
  const allItems = [
    ...(day?.places ?? []).map((p) => ({ type: 'place' as const, item: p })),
    ...(day?.transports ?? []).map((tr) => ({ type: 'transport' as const, item: tr })),
    ...(day?.customs ?? []).map((c) => ({ type: 'custom' as const, item: c })),
  ].sort(
    (a, b) =>
      timeToMinutes(a.item.startTime ?? DEFAULT_START_TIME) -
      timeToMinutes(b.item.startTime ?? DEFAULT_START_TIME),
  );
  if (!allItems.length) return DEFAULT_START_TIME;
  const last = allItems[allItems.length - 1]!;
  const start = last.item.startTime ?? DEFAULT_START_TIME;
  const defaultDuration = last.type === 'transport' ? 30 : 60;
  const end = last.item.endTime ?? addMinutes(start, defaultDuration);
  // Defensive re-pad: addMinutes always emits HH:MM, but a stored endTime
  // could in theory round-trip through `days: z.unknown()` on the server
  // with an unpadded value (e.g. legacy import, manual edit). Normalize
  // here so the next item's startTime is canonical.
  const [h, m] = parseTime(end);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
